import { useEffect, useState, useCallback, useRef, useMemo } from "react"
import { useTranslations } from "next-intl"
import { Link } from "react-router-dom"
import {
	Users, Search, Mail, Phone, Building2, User, Copy, Check,
	ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ExternalLink,
	AlertTriangle, CircleCheck, Loader2, FileText, Calendar,
	Send, X, CheckCircle2, ScanSearch, Brain,
} from "lucide-react"
import { DashboardLayout } from "@/components/common/DashboardLayout"
import { LoadingSpinner } from "@/components/common/LoadingSpinner"
import { SourceBadge } from "@/components/common/SourceBadge"
import { FilterSheet, FilterTriggerButton } from "@/components/ui/filter-sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import {
	Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet"
import { api } from "@/lib/apiService"
import type { TenderContact, Organization } from "@/types/tender.types"
import type { FilterGroupDefinition, FilterSelections } from "@/types/filters"

interface ContactWithTender extends TenderContact {
	source_title: string
	source_reference: string
	source_deadline?: string | null
	source_score?: number | null
	tender_name: string  // legacy alias for source_title
	tender_expedient: string  // legacy alias for source_reference
}

interface TenderSummary {
	id: string
	name: string
	expedient: string
	status: string
	budget_no_taxes: number | null
	submission_deadline: string | null
}

interface GrantSummary {
	id: string
	codigo_bdns: string
	descripcion: string | null
	abierto: boolean
	presupuesto_total: number | null
	fecha_fin_solicitud: string | null
}

interface OrganizationDetail {
	organization: { id: string; name: string; emails: string[]; created_at: string }
	contacts: (ContactWithTender)[]
	tenders: TenderSummary[]
	grants: GrantSummary[]
}

interface ContactDetailData {
	contact: TenderContact & { source_title: string; source_reference: string; tender_name: string; tender_expedient: string; organization_name: string | null }
	tenders: TenderSummary[]
	grants: GrantSummary[]
}

interface ContactsListResponse {
	success: boolean
	data: ContactWithTender[]
	total: number
	page: number
	limit: number
	pages: number
}

interface OrganizationsListResponse {
	success: boolean
	data: (Organization & { contact_count: number })[]
	total: number
	page: number
	limit: number
	pages: number
}

interface EmailSearchProgress {
	running: boolean
	totalContacts: number
	processedContacts: number
	found: number
	notFound: number
	errors: number
	percentage: number
	startedAt: string | null
}

interface TenderOption {
	id: string
	name: string
	expedient: string
}

interface PipedriveSyncResult {
	total: number
	synced: number
	failed: number
	results: Array<{
		contactId: string
		contactName: string
		success: boolean
		error?: string
	}>
}

function CopyButton({ text }: { text: string }) {
	const t = useTranslations("contacts")
	const [copied, setCopied] = useState(false)

	const handleCopy = (e: React.MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()
		navigator.clipboard.writeText(text)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}

	return (
		<button
			onClick={handleCopy}
			className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-[#2D8A5E] transition-colors"
			aria-label={t("copied")}
		>
			{copied ? <Check className="h-3 w-3 text-[#2D8A5E]" /> : <Copy className="h-3 w-3" />}
		</button>
	)
}

function Pagination({ page, pages, total, limit, onPageChange }: {
	page: number; pages: number; total: number; limit: number; onPageChange: (page: number) => void
}) {
	const t = useTranslations("contacts")
	if (pages <= 1) return null
	const from = (page - 1) * limit + 1
	const to = Math.min(page * limit, total)

	return (
		<div className="flex items-center justify-between gap-4 flex-wrap">
			<p className="text-sm text-muted-foreground">
				{t("paginationRange", { from, to, total })}
			</p>
			<div className="flex items-center gap-1">
				<Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onPageChange(1)} aria-label="First page">
					<ChevronsLeft className="h-4 w-4" />
				</Button>
				<Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)} aria-label="Previous page">
					<ChevronLeft className="h-4 w-4" />
				</Button>
				<span className="text-sm px-3">{page} / {pages}</span>
				<Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => onPageChange(page + 1)} aria-label="Next page">
					<ChevronRight className="h-4 w-4" />
				</Button>
				<Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => onPageChange(pages)} aria-label="Last page">
					<ChevronsRight className="h-4 w-4" />
				</Button>
			</div>
		</div>
	)
}

export default function ContactsPage() {
	const t = useTranslations("contacts")
	const [emailSearchStatus, setEmailSearchStatus] = useState<EmailSearchProgress | null>(null)
	const [isSearchStarting, setIsSearchStarting] = useState(false)
	const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

	const stopPolling = useCallback(() => {
		if (pollIntervalRef.current) {
			clearInterval(pollIntervalRef.current)
			pollIntervalRef.current = null
		}
	}, [])

	const startPolling = useCallback(() => {
		stopPolling()
		pollIntervalRef.current = setInterval(async () => {
			try {
				const res = await api.get<{ success: boolean; data: EmailSearchProgress }>("/tenders/contacts/search-emails/status")
				if (res.success) {
					setEmailSearchStatus(res.data)
					if (!res.data.running) stopPolling()
				}
			} catch {
				stopPolling()
			}
		}, 2000)
	}, [stopPolling])

	const startEmailSearch = useCallback(async () => {
		setIsSearchStarting(true)
		try {
			await api.post<{ success: boolean }>("/tenders/contacts/search-emails", {})
			startPolling()
		} catch (err) {
			console.error("Failed to start email search:", err)
		} finally {
			setIsSearchStarting(false)
		}
	}, [startPolling])

	// Check if search is already running on mount; cleanup on unmount
	useEffect(() => {
		const checkStatus = async () => {
			try {
				const res = await api.get<{ success: boolean; data: EmailSearchProgress }>("/tenders/contacts/search-emails/status")
				if (res.success && res.data.running) {
					setEmailSearchStatus(res.data)
					startPolling()
				}
			} catch {}
		}
		checkStatus()
		return stopPolling
	}, [startPolling, stopPolling])

	return (
		<DashboardLayout>
			{/* Header */}
			<div>
				<h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
				<p className="text-sm text-muted-foreground mt-1">{t("subtitle")}</p>
			</div>

			<Tabs defaultValue="contacts" className="space-y-4">
				<div className="flex items-center justify-between">
					<TabsList>
						<TabsTrigger value="contacts" className="gap-1.5">
							<User className="h-4 w-4" />
							{t("tabContacts")}
						</TabsTrigger>
						<TabsTrigger value="organizations" className="gap-1.5">
							<Building2 className="h-4 w-4" />
							{t("tabOrganizations")}
						</TabsTrigger>
					</TabsList>

					<Button
						size="sm"
						variant="outline"
						className="gap-2"
						disabled={emailSearchStatus?.running === true || isSearchStarting}
						onClick={startEmailSearch}
					>
						{emailSearchStatus?.running || isSearchStarting
							? <Loader2 className="h-3.5 w-3.5 animate-spin" />
							: <ScanSearch className="h-3.5 w-3.5" />
						}
						{t("discoverEmails")}
					</Button>
				</div>

				{/* Email search progress banner */}
				{emailSearchStatus?.running && (
					<div className="rounded-lg border border-blue-200 bg-blue-50 overflow-hidden">
						<div className="flex items-center gap-3 p-3 text-sm">
							<Loader2 className="h-4 w-4 animate-spin text-blue-600 shrink-0" />
							<div className="flex-1 min-w-0">
								<span className="text-blue-800">
									{t("searchEmailsProgress", {
										found: emailSearchStatus.found,
										notFound: emailSearchStatus.notFound,
										processed: emailSearchStatus.processedContacts,
										total: emailSearchStatus.totalContacts,
									})}
								</span>
								<span className="text-blue-600 ml-2 font-medium">
									{emailSearchStatus.percentage}%
								</span>
							</div>
						</div>
						<div className="h-1.5 bg-blue-100">
							<div
								className="h-full bg-blue-500 transition-all duration-500 ease-out"
								style={{ width: `${emailSearchStatus.percentage}%` }}
							/>
						</div>
					</div>
				)}
				{emailSearchStatus && !emailSearchStatus.running && emailSearchStatus.found > 0 && (
					<div className="flex items-center gap-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50 text-sm">
						<CircleCheck className="h-4 w-4 text-emerald-600 shrink-0" />
						<span className="text-emerald-800">
							{t("searchEmailsDone", {
								found: emailSearchStatus.found,
								notFound: emailSearchStatus.notFound,
							})}
						</span>
						<button
							onClick={() => setEmailSearchStatus(null)}
							className="ml-auto text-emerald-600 hover:text-emerald-800 text-xs font-bold"
							aria-label="Dismiss"
						>
							<X className="h-4 w-4" />
						</button>
					</div>
				)}

				<TabsContent value="contacts">
					<ContactsTab />
				</TabsContent>

				<TabsContent value="organizations">
					<OrganizationsTab />
				</TabsContent>
			</Tabs>
		</DashboardLayout>
	)
}

// ── Contacts Tab with Pipedrive integration ──

function ContactsTab() {
	const t = useTranslations("contacts")
	const tc = useTranslations("common")
	const [contacts, setContacts] = useState<ContactWithTender[]>([])
	const [total, setTotal] = useState(0)
	const [pages, setPages] = useState(1)
	const [isLoading, setIsLoading] = useState(true)
	const [search, setSearch] = useState("")
	const [page, setPage] = useState(1)
	const [selectedContactId, setSelectedContactId] = useState<string | null>(null)

	// FilterSheet state
	const [filterOpen, setFilterOpen] = useState(false)
	const [filters, setFilters] = useState({
		emailType: [] as string[],
		sourceType: [] as string[],
		tenderIds: [] as string[],
		syncStatus: [] as string[],
		deadlineWithinDays: "",
		minRelevanceScore: "",
		maxRelevanceScore: "",
	})

	// Pipedrive integration state
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
	const [tenderOptions, setTenderOptions] = useState<TenderOption[]>([])
	const [isSyncing, setIsSyncing] = useState(false)
	const [syncResult, setSyncResult] = useState<PipedriveSyncResult | null>(null)
	const [showConfirm, setShowConfirm] = useState(false)
	const [syncingContactId, setSyncingContactId] = useState<string | null>(null)
	const [singleSyncError, setSingleSyncError] = useState<string | null>(null)

	const limit = 30

	// Fetch tenders for the filter dropdown
	useEffect(() => {
		api.get<{ success: boolean; data: TenderOption[] }>("/tenders/with-contacts")
			.then((res) => setTenderOptions(res.data))
			.catch(() => {})
	}, [])

	// FilterSheet groups
	const filterGroups: FilterGroupDefinition[] = useMemo(() => [
		{
			key: "emailType",
			label: t("filterPanel.emailType"),
			icon: Mail,
			type: "multi-select" as const,
			options: [
				{ value: "has_email", label: t("filterPanel.emailHas") },
				{ value: "no_email", label: t("filterPanel.emailNo") },
			],
		},
		{
			key: "sourceType",
			label: "Origen",
			icon: FileText,
			type: "multi-select" as const,
			options: [
				{ value: "tender", label: "Licitació" },
				{ value: "grant", label: "Subvenció" },
			],
		},
		{
			key: "tenderIds",
			label: t("filterPanel.tender"),
			icon: FileText,
			type: "multi-select" as const,
			options: tenderOptions.map((o) => ({
				value: o.id,
				label: `${o.expedient} — ${o.name.slice(0, 60)}`,
			})),
		},
		{
			key: "syncStatus",
			label: t("filterPanel.syncStatus"),
			icon: Send,
			type: "multi-select" as const,
			options: [
				{ value: "pending", label: t("filterPanel.syncPending") },
				{ value: "synced", label: t("filterPanel.syncDone") },
			],
		},
		{
			key: "deadline",
			label: t("filterPanel.deadline"),
			icon: Calendar,
			type: "number-range" as const,
			options: [],
			numberRangeLabels: {
				min: t("filterPanel.from"),
				max: t("filterPanel.deadlineDaysLabel"),
				minPlaceholder: "0",
				maxPlaceholder: "∞",
			},
		},
		{
			key: "relevanceScore",
			label: t("filterPanel.relevanceScore"),
			icon: Brain,
			type: "number-range" as const,
			options: [],
			numberRangeLabels: {
				min: t("filterPanel.relevanceMin"),
				max: t("filterPanel.relevanceMax"),
				minPlaceholder: "0",
				maxPlaceholder: "100",
			},
		},
	], [t, tenderOptions])

	const filterSelections = useMemo((): FilterSelections => ({
		emailType: filters.emailType,
		sourceType: filters.sourceType,
		tenderIds: filters.tenderIds,
		syncStatus: filters.syncStatus,
		deadline: ["", filters.deadlineWithinDays],
		relevanceScore: [filters.minRelevanceScore, filters.maxRelevanceScore],
	}), [filters])

	const activeFilterCount = useMemo(() => {
		let count = 0
		if (filters.emailType.length > 0) count++
		if (filters.sourceType.length > 0) count++
		if (filters.tenderIds.length > 0) count++
		if (filters.syncStatus.length > 0) count++
		if (filters.deadlineWithinDays) count++
		if (filters.minRelevanceScore || filters.maxRelevanceScore) count++
		return count
	}, [filters])

	const handleApplyFilters = (selections: FilterSelections) => {
		setFilters({
			emailType: selections.emailType ?? [],
			sourceType: selections.sourceType ?? [],
			tenderIds: selections.tenderIds ?? [],
			syncStatus: selections.syncStatus ?? [],
			deadlineWithinDays: selections.deadline?.[1] ?? "",
			minRelevanceScore: selections.relevanceScore?.[0] ?? "",
			maxRelevanceScore: selections.relevanceScore?.[1] ?? "",
		})
		setPage(1)
	}

	const handleClearFilters = () => {
		setFilters({
			emailType: [],
			sourceType: [],
			tenderIds: [],
			syncStatus: [],
			deadlineWithinDays: "",
			minRelevanceScore: "",
			maxRelevanceScore: "",
		})
		setPage(1)
	}

	const fetchContacts = useCallback(async () => {
		setIsLoading(true)
		try {
			const params = new URLSearchParams()
			if (search) params.set("search", search)
			if (filters.emailType.length === 1) params.set("emailType", filters.emailType[0])
			if (filters.sourceType.length === 1) params.set("sourceType", filters.sourceType[0])
			filters.tenderIds.forEach((id) => params.append("tenderId", id))
			if (filters.syncStatus.length === 1) params.set("syncStatus", filters.syncStatus[0])
			if (filters.deadlineWithinDays) params.set("deadlineWithinDays", filters.deadlineWithinDays)
			if (filters.minRelevanceScore) params.set("minRelevanceScore", filters.minRelevanceScore)
			if (filters.maxRelevanceScore) params.set("maxRelevanceScore", filters.maxRelevanceScore)
			params.set("page", page.toString())
			params.set("limit", limit.toString())

			const res = await api.get<ContactsListResponse>(`/tenders/contacts/all?${params}`)
			setContacts(res.data)
			setTotal(res.total)
			setPages(res.pages)
		} catch {
			// ignore
		} finally {
			setIsLoading(false)
		}
	}, [search, filters, page])

	useEffect(() => {
		const timer = setTimeout(fetchContacts, search ? 300 : 0)
		return () => clearTimeout(timer)
	}, [fetchContacts])

	// Clear selection when filters or page change
	useEffect(() => {
		setSelectedIds(new Set())
		setAllFilteredSelected(false)
	}, [search, filters, page])

	// Selection handlers
	const selectableContacts = contacts.filter((c) => c.email && c.email_verified && !c.pipedrive_synced_at)
	const [allFilteredSelected, setAllFilteredSelected] = useState(false)
	const [isLoadingAllIds, setIsLoadingAllIds] = useState(false)

	const toggleContact = (id: string) => {
		setAllFilteredSelected(false)
		setSelectedIds((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	const toggleAll = () => {
		setAllFilteredSelected(false)
		const allCurrentSelected = selectableContacts.every((c) => selectedIds.has(c.id))
		if (allCurrentSelected && selectableContacts.length > 0) {
			setSelectedIds(new Set())
		} else {
			setSelectedIds(new Set(selectableContacts.map((c) => c.id)))
		}
	}

	const selectAllFiltered = async () => {
		setIsLoadingAllIds(true)
		try {
			const params = new URLSearchParams()
			if (search) params.set("search", search)
			if (filters.emailType.length === 1) params.set("emailType", filters.emailType[0])
			if (filters.sourceType.length === 1) params.set("sourceType", filters.sourceType[0])
			filters.tenderIds.forEach((id) => params.append("tenderId", id))
			if (filters.syncStatus.length === 1) params.set("syncStatus", filters.syncStatus[0])
			if (filters.deadlineWithinDays) params.set("deadlineWithinDays", filters.deadlineWithinDays)
			if (filters.minRelevanceScore) params.set("minRelevanceScore", filters.minRelevanceScore)

			const res = await api.get<{ success: boolean; data: string[]; total: number }>(
				`/tenders/contacts/selectable-ids?${params}`
			)
			setSelectedIds(new Set(res.data))
			setAllFilteredSelected(true)
		} catch {
			// fallback: keep current selection
		} finally {
			setIsLoadingAllIds(false)
		}
	}

	// Any sync operation in progress
	const isAnySyncing = isSyncing || syncingContactId !== null

	// Individual sync
	const syncSingleContact = async (contactId: string) => {
		if (isAnySyncing) return
		setSyncingContactId(contactId)
		setSingleSyncError(null)
		try {
			await api.post<{ success: boolean }>("/pipedrive/sync", { contactId })
			fetchContacts()
		} catch (error) {
			const msg = error instanceof Error ? error.message : "Error"
			setSingleSyncError(msg)
			setTimeout(() => setSingleSyncError(null), 5000)
		} finally {
			setSyncingContactId(null)
		}
	}

	// Bulk sync — one by one with progress tracking
	const [syncProgress, setSyncProgress] = useState<{ current: number; total: number; synced: number; failed: number } | null>(null)
	const syncErrors = syncResult?.results.filter((result) => !result.success && result.error).slice(0, 3) || []

	const syncSelectedContacts = async () => {
		if (isSyncing) return
		setShowConfirm(false)
		setIsSyncing(true)
		setSyncResult(null)

		const ids = Array.from(selectedIds)
		const progress = { current: 0, total: ids.length, synced: 0, failed: 0 }
		setSyncProgress({ ...progress })

		const results: PipedriveSyncResult["results"] = []

		for (const contactId of ids) {
			progress.current++
			setSyncProgress({ ...progress })

			try {
				await api.post<{ success: boolean }>("/pipedrive/sync", { contactId })
				progress.synced++
				results.push({ contactId, contactName: "", success: true })
			} catch (error) {
				const message = error instanceof Error ? error.message : "Failed"
				progress.failed++
				results.push({ contactId, contactName: "", success: false, error: message })
			}

			setSyncProgress({ ...progress })
		}

		setSyncResult({ total: ids.length, synced: progress.synced, failed: progress.failed, results })
		setSyncProgress(null)
		setSelectedIds(new Set())
		setAllFilteredSelected(false)
		setIsSyncing(false)
		fetchContacts()
	}

	return (
		<div className="space-y-4">

			{/* Search + Filter button + Select all */}
			<div className="flex items-center gap-2.5 flex-wrap">
				<div className="relative min-w-45 max-w-xs flex-1">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						value={search}
						onChange={(e) => { setSearch(e.target.value); setPage(1) }}
						placeholder={t("searchPlaceholder")}
						className="pl-10 h-9"
					/>
				</div>

				<FilterTriggerButton
					activeCount={activeFilterCount}
					label={t("filterPanel.title")}
					onClick={() => setFilterOpen(true)}
				/>

				{/* Select all + count */}
				<div className="flex items-center gap-3 ml-auto">
					{selectableContacts.length > 0 && (
						<div className="flex items-center gap-2">
							<Checkbox
								checked={selectableContacts.every((c) => selectedIds.has(c.id))}
								onCheckedChange={toggleAll}
								id="select-all"
							/>
							<label htmlFor="select-all" className="text-xs text-muted-foreground cursor-pointer">
								{t("selectAll")}
							</label>
						</div>
					)}
					<p className="text-xs text-muted-foreground">
						{t("total", { count: total })}
					</p>
				</div>
			</div>

			{/* "Select all filtered" banner — shows when all on current page are selected but there are more */}
			{selectableContacts.length > 0
				&& selectableContacts.every((c) => selectedIds.has(c.id))
				&& !allFilteredSelected
				&& total > selectableContacts.length && (
				<div className="flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-[#2D8A5E]/20 bg-[#2D8A5E]/5 text-sm">
					<span className="text-gray-600">
						{t("selectAllPageSelected", { count: selectableContacts.length })}
					</span>
					<button
						onClick={selectAllFiltered}
						disabled={isLoadingAllIds}
						className="font-medium text-[#2D8A5E] hover:text-[#246f4b] underline underline-offset-2 disabled:opacity-50"
					>
						{isLoadingAllIds
							? t("pipedriveSyncing")
							: t("selectAllFiltered", { count: total })
						}
					</button>
				</div>
			)}

			{allFilteredSelected && (
				<div className="flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-[#2D8A5E]/20 bg-[#2D8A5E]/5 text-sm">
					<CheckCircle2 className="h-4 w-4 text-[#2D8A5E]" />
					<span className="text-[#2D8A5E] font-medium">
						{t("selectAllFilteredDone", { count: selectedIds.size })}
					</span>
				</div>
			)}

			{/* Sync result banner */}
			{syncResult && (
				<div className={`flex items-center gap-3 p-3 rounded-lg border text-sm ${
					syncResult.failed === 0
						? "border-emerald-200 bg-emerald-50"
						: "border-amber-200 bg-amber-50"
				}`}>
					{syncResult.failed === 0 ? (
						<>
							<CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
							<span className="text-emerald-800">
								{t("pipedriveSyncSuccess", { synced: syncResult.synced })}
							</span>
						</>
					) : (
						<>
							<AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
							<div className="min-w-0 text-amber-800">
								<p>{t("pipedriveSyncPartial", { synced: syncResult.synced, failed: syncResult.failed })}</p>
								{syncErrors.length > 0 && (
									<p className="mt-1 truncate text-xs text-amber-700">
										{syncErrors.map((result) => result.error).join(" · ")}
									</p>
								)}
							</div>
						</>
					)}
					<button
						onClick={() => setSyncResult(null)}
						className="ml-auto text-gray-500 hover:text-gray-700"
						aria-label="Dismiss"
					>
						<X className="h-4 w-4" />
					</button>
				</div>
			)}

			{/* Single sync error banner */}
			{singleSyncError && (
				<div className="flex items-center gap-3 p-3 rounded-lg border border-red-200 bg-red-50 text-sm">
					<AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
					<span className="text-red-800">{t("pipedriveSyncError")}: {singleSyncError}</span>
					<button
						onClick={() => setSingleSyncError(null)}
						className="ml-auto text-gray-500 hover:text-gray-700"
						aria-label="Dismiss"
					>
						<X className="h-4 w-4" />
					</button>
				</div>
			)}

			{/* Table */}
			{isLoading ? (
				<div className="flex justify-center py-16"><LoadingSpinner size="lg" /></div>
			) : contacts.length === 0 ? (
				<div className="text-center py-16">
					<Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
					<p className="text-muted-foreground">{t("empty")}</p>
				</div>
			) : (
				<div className="space-y-4">
					{/* Contact cards */}
					<div className="space-y-2">
						{contacts.map((contact) => (
							<ContactRow
								key={contact.id}
								contact={contact}
								isSelected={selectedIds.has(contact.id)}
								isSyncing={syncingContactId === contact.id}
								isAnySyncing={isAnySyncing}
								onToggle={() => toggleContact(contact.id)}
								onSync={() => syncSingleContact(contact.id)}
								onClick={() => setSelectedContactId(contact.id)}
							/>
						))}
					</div>

					<Pagination
						page={page}
						pages={pages}
						total={total}
						limit={limit}
						onPageChange={setPage}
					/>
				</div>
			)}

			{/* Floating action bar when contacts are selected */}
			{(selectedIds.size > 0 || syncProgress) && !showConfirm && (
				<div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
					<div className="flex items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-xl shadow-2xl">
						{syncProgress ? (
							<>
								<Loader2 className="h-4 w-4 animate-spin text-[#2D8A5E] shrink-0" />
								<div className="flex flex-col gap-1">
									<span className="text-sm font-medium tabular-nums">
										{syncProgress.current}/{syncProgress.total}
									</span>
									<div className="h-1.5 w-40 bg-gray-700 rounded-full overflow-hidden">
										<div
											className="h-full bg-[#2D8A5E] rounded-full transition-all duration-300"
											style={{ width: `${Math.round((syncProgress.current / syncProgress.total) * 100)}%` }}
										/>
									</div>
								</div>
								<div className="flex items-center gap-2 text-xs tabular-nums">
									{syncProgress.synced > 0 && (
										<span className="text-emerald-400">{syncProgress.synced} ok</span>
									)}
									{syncProgress.failed > 0 && (
										<span className="text-red-400">{syncProgress.failed} err</span>
									)}
								</div>
							</>
						) : (
							<>
								<span className="text-sm font-medium">
									{t("selected", { count: selectedIds.size })}
								</span>
								<div className="w-px h-5 bg-gray-600" />
								<Button
									size="sm"
									className="bg-[#2D8A5E] hover:bg-[#246f4b] text-white gap-2"
									disabled={isAnySyncing}
									onClick={() => setShowConfirm(true)}
								>
									<Send className="h-3.5 w-3.5" />
									{t("pipedriveSyncBulk", { count: selectedIds.size })}
								</Button>
								<button
									onClick={() => { setSelectedIds(new Set()); setAllFilteredSelected(false) }}
									className="text-gray-400 hover:text-white transition-colors"
									aria-label="Clear selection"
								>
									<X className="h-4 w-4" />
								</button>
							</>
						)}
					</div>
				</div>
			)}

			{/* Confirmation dialog */}
			{showConfirm && (
				<div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
					<div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 space-y-4">
						<div className="flex items-center gap-3">
							<div className="h-10 w-10 rounded-full bg-[#2D8A5E]/10 flex items-center justify-center">
								<Send className="h-5 w-5 text-[#2D8A5E]" />
							</div>
							<h3 className="text-lg font-semibold text-gray-900">{t("pipedriveConfirmTitle")}</h3>
						</div>
						<p className="text-sm text-gray-600">
							{t("pipedriveConfirmMessage", { count: selectedIds.size })}
						</p>
						<div className="flex items-center justify-end gap-3 pt-2">
							<Button variant="outline" onClick={() => setShowConfirm(false)}>
								{t("pipedriveCancel")}
							</Button>
							<Button
								className="bg-[#2D8A5E] hover:bg-[#246f4b] text-white gap-2"
								disabled={isSyncing}
								onClick={syncSelectedContacts}
							>
								<Send className="h-4 w-4" />
								{t("pipedriveConfirmButton")}
							</Button>
						</div>
					</div>
				</div>
			)}

			<ContactDetailSheet
				contactId={selectedContactId}
				onClose={() => setSelectedContactId(null)}
			/>

			{/* Filter Sheet */}
			<FilterSheet
				open={filterOpen}
				onOpenChange={setFilterOpen}
				title={t("filterPanel.title")}
				groups={filterGroups}
				selections={filterSelections}
				onApply={handleApplyFilters}
				onClearAll={handleClearFilters}
				applyLabel={t("filterPanel.apply")}
				clearAllLabel={t("filterPanel.clear")}
				selectPlaceholder={tc("selectPlaceholder")}
				searchPlaceholder={tc("searchPlaceholder")}
				noResultsLabel={tc("noResults")}
			/>
		</div>
	)
}

// ── Contact Row with Pipedrive ──

function ContactRow({
	contact,
	isSelected,
	isSyncing,
	isAnySyncing,
	onToggle,
	onSync,
	onClick,
}: {
	contact: ContactWithTender
	isSelected: boolean
	isSyncing: boolean
	isAnySyncing: boolean
	onToggle: () => void
	onSync: () => void
	onClick: () => void
}) {
	const t = useTranslations("contacts")
	const isSynced = !!contact.pipedrive_synced_at
	const canSelect = !!contact.email && !isSynced

	return (
		<div
			className={`flex items-start gap-3 p-4 rounded-lg border transition-colors cursor-pointer ${
				isSelected
					? "border-[#2D8A5E]/40 bg-[#2D8A5E]/5"
					: "border-gray-100 hover:border-gray-200 hover:bg-gray-50/50"
			}`}
			onClick={onClick}
		>
			{/* Checkbox */}
			<div className="flex items-center pt-0.5" onClick={(e) => e.stopPropagation()}>
				{canSelect ? (
					<Checkbox
						checked={isSelected}
						onCheckedChange={onToggle}
					/>
				) : (
					<div className="h-4 w-4" />
				)}
			</div>

			<div className="h-9 w-9 rounded-full bg-[#2D8A5E]/10 flex items-center justify-center shrink-0">
				<User className="h-4 w-4 text-[#2D8A5E]" />
			</div>

			<div className="flex-1 min-w-0 space-y-1">
				<div className="flex items-center gap-2 flex-wrap">
					<p className="font-semibold text-gray-900 text-sm capitalize">{contact.name.toLowerCase()}</p>
					{contact.role && (
						<span className="text-xs text-muted-foreground">- {contact.role}</span>
					)}
					{/* Pipedrive sync badge */}
					{isSynced ? (
						<Badge variant="outline" className="text-[10px] text-green-700 border-green-200 bg-green-50 gap-1">
							<CheckCircle2 className="h-3 w-3" />
							{t("pipedriveSynced")}
						</Badge>
					) : contact.email ? (
						<Badge variant="outline" className="text-[10px] text-gray-500 border-gray-200 gap-1">
							{t("pipedrivePending")}
						</Badge>
					) : null}
				</div>

				{contact.organization && (
					<p className="text-xs text-muted-foreground flex items-center gap-1">
						<Building2 className="h-3 w-3" />
						{contact.organization}
					</p>
				)}

				{/* Email row */}
				<div className="flex items-center gap-3 flex-wrap">
					{contact.email && (
						<span className="inline-flex items-center gap-1.5 text-sm">
							<Mail className="h-3.5 w-3.5 text-[#2D8A5E]" />
							<a href={`mailto:${contact.email}`} className="text-[#2D8A5E] hover:underline">{contact.email}</a>
							{contact.email_verified && (
								<span title="Verified"><CircleCheck className="h-3.5 w-3.5 text-emerald-500" /></span>
							)}
							<CopyButton text={contact.email} />
						</span>
					)}
					{contact.phone && (
						<span className="inline-flex items-center gap-1.5 text-sm">
							<Phone className="h-3.5 w-3.5 text-gray-400" />
							<a href={`tel:${contact.phone}`} className="text-gray-700 hover:underline">{contact.phone}</a>
						</span>
					)}
				</div>
			</div>

			{/* Right side: source link + Pipedrive button */}
			<div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
				<SourceBadge type={contact.source_type} />
				<Link
					to={contact.source_type === "grant" ? `/grants/${contact.source_id}` : `/tenders/${contact.source_id}`}
					className="text-xs text-muted-foreground hover:text-[#2D8A5E] flex items-center gap-1 max-w-[160px]"
					title={contact.source_title}
				>
					<ExternalLink className="h-3 w-3 shrink-0" />
					<span className="truncate">{contact.source_reference}</span>
				</Link>

				{/* Individual Pipedrive sync button */}
				{contact.email && !isSynced && (
					<Button
						variant="outline"
						size="sm"
						className="h-7 px-2 text-xs gap-1.5 border-[#2D8A5E]/30 text-[#2D8A5E] hover:bg-[#2D8A5E]/5"
						disabled={isAnySyncing}
						onClick={onSync}
					>
						{isSyncing
							? <Loader2 className="h-3 w-3 animate-spin" />
							: <Send className="h-3 w-3" />
						}
						Pipedrive
					</Button>
				)}
			</div>
		</div>
	)
}

// ── Organizations Tab ──

function OrganizationsTab() {
	const t = useTranslations("contacts")
	const [organizations, setOrganizations] = useState<(Organization & { contact_count: number })[]>([])
	const [total, setTotal] = useState(0)
	const [pages, setPages] = useState(1)
	const [isLoading, setIsLoading] = useState(true)
	const [search, setSearch] = useState("")
	const [filter, setFilter] = useState<"all" | "with_email" | "no_email">("all")
	const [page, setPage] = useState(1)
	const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)

	const limit = 30

	const fetchOrganizations = useCallback(async () => {
		setIsLoading(true)
		try {
			const params = new URLSearchParams()
			if (search) params.set("search", search)
			if (filter !== "all") params.set("filter", filter)
			params.set("page", page.toString())
			params.set("limit", limit.toString())

			const res = await api.get<OrganizationsListResponse>(`/tenders/organizations?${params}`)
			setOrganizations(res.data)
			setTotal(res.total)
			setPages(res.pages)
		} catch {
			// ignore
		} finally {
			setIsLoading(false)
		}
	}, [search, filter, page])

	useEffect(() => {
		const timer = setTimeout(fetchOrganizations, search ? 300 : 0)
		return () => clearTimeout(timer)
	}, [fetchOrganizations])

	return (
		<div className="space-y-4">
			{/* Filters */}
			<div className="flex items-center gap-3 flex-wrap">
				<div className="relative flex-1 min-w-[200px] max-w-md">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						value={search}
						onChange={(e) => { setSearch(e.target.value); setPage(1) }}
						placeholder={t("orgSearchPlaceholder")}
						className="pl-10"
					/>
				</div>
				<div className="flex items-center gap-1 rounded-lg border border-gray-200 p-0.5">
					{([
						{ key: "all" as const, icon: null, activeClass: "bg-gray-100 text-gray-800 border border-gray-200" },
						{ key: "with_email" as const, icon: <Mail className="h-3 w-3" />, activeClass: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
						{ key: "no_email" as const, icon: <AlertTriangle className="h-3 w-3" />, activeClass: "bg-red-50 text-red-700 border border-red-200" },
					]).map(({ key, icon, activeClass }) => (
						<button
							key={key}
							onClick={() => { setFilter(key); setPage(1) }}
							className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
								filter === key
									? activeClass
									: "text-muted-foreground hover:text-gray-700 hover:bg-gray-50 border border-transparent"
							}`}
						>
							{icon}
							{t(`filter_${key}`)}
						</button>
					))}
				</div>
				<p className="text-sm text-muted-foreground ml-auto">
					{t("orgTotal", { count: total })}
				</p>
			</div>

			{/* List */}
			{isLoading ? (
				<div className="flex justify-center py-16"><LoadingSpinner size="lg" /></div>
			) : organizations.length === 0 ? (
				<div className="text-center py-16">
					<Building2 className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
					<p className="text-muted-foreground">{t("orgEmpty")}</p>
				</div>
			) : (
				<div className="space-y-4">
					<div className="space-y-2">
						{organizations.map((org) => (
							<OrganizationRow key={org.id} organization={org} onClick={() => setSelectedOrgId(org.id)} />
						))}
					</div>

					<Pagination
						page={page}
						pages={pages}
						total={total}
						limit={limit}
						onPageChange={setPage}
					/>
				</div>
			)}

			<OrganizationDetailSheet
				orgId={selectedOrgId}
				onClose={() => setSelectedOrgId(null)}
			/>
		</div>
	)
}

function OrganizationRow({ organization, onClick }: { organization: Organization & { contact_count: number }; onClick: () => void }) {
	const t = useTranslations("contacts")

	return (
		<div
			className="flex items-start gap-3 p-4 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 transition-colors cursor-pointer"
			onClick={onClick}
		>
			<div className="h-9 w-9 rounded-full bg-[#2D8A5E]/10 flex items-center justify-center shrink-0">
				<Building2 className="h-4 w-4 text-[#2D8A5E]" />
			</div>

			<div className="flex-1 min-w-0 space-y-1.5">
				<p className="font-semibold text-gray-900 text-sm">{organization.name}</p>

				{/* Emails */}
				{organization.emails && organization.emails.length > 0 && (
					<div className="flex items-center gap-2 flex-wrap">
						{organization.emails.map((email) => (
							<span key={email} className="inline-flex items-center gap-1.5 text-sm">
								<Mail className="h-3.5 w-3.5 text-[#2D8A5E]" />
								<a href={`mailto:${email}`} className="text-[#2D8A5E] hover:underline">{email}</a>
								<CopyButton text={email} />
							</span>
						))}
					</div>
				)}
			</div>

			{/* Right side: stats */}
			<div className="flex items-center gap-3 shrink-0">
				{organization.emails && organization.emails.length > 0 && (
					<Badge variant="outline" className="text-xs text-[#2D8A5E] border-[#2D8A5E]/20">
						{t("orgEmails", { count: organization.emails.length })}
					</Badge>
				)}
				<Badge variant="outline" className="text-xs">
					<Users className="h-3 w-3 mr-1" />
					{t("orgContacts", { count: organization.contact_count })}
				</Badge>
			</div>
		</div>
	)
}

// ── Detail Sheets ──

function formatBudget(amount: number | null | undefined): string {
	if (amount === null || amount === undefined) return "—"
	return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(amount)
}

function formatDate(date: string | null | undefined): string {
	if (!date) return "—"
	return new Date(date).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })
}

function GrantListItem({ grant }: { grant: GrantSummary }) {
	return (
		<Link
			to={`/grants/${grant.id}`}
			className="block p-3 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 transition-colors"
		>
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0 flex-1">
					<p className="text-sm font-medium text-gray-900 line-clamp-2">
						{grant.descripcion || grant.codigo_bdns}
					</p>
					<p className="text-xs text-muted-foreground mt-0.5">{grant.codigo_bdns}</p>
				</div>
				<Badge
					variant="outline"
					className={`text-[10px] shrink-0 ${grant.abierto ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-50 text-gray-600 border-gray-200"}`}
				>
					{grant.abierto ? "Oberta" : "Tancada"}
				</Badge>
			</div>
			<div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
				{grant.presupuesto_total !== null && grant.presupuesto_total !== undefined && (
					<span className="flex items-center gap-1">
						<FileText className="h-3 w-3" />
						{formatBudget(grant.presupuesto_total)}
					</span>
				)}
				{grant.fecha_fin_solicitud && (
					<span className="flex items-center gap-1">
						<Calendar className="h-3 w-3" />
						{formatDate(grant.fecha_fin_solicitud)}
					</span>
				)}
			</div>
		</Link>
	)
}

function TenderListItem({ tender }: { tender: TenderSummary }) {
	const statusColors: Record<string, string> = {
		Publicada: "bg-emerald-50 text-emerald-700 border-emerald-200",
		Adjudicada: "bg-blue-50 text-blue-700 border-blue-200",
		Resuelta: "bg-gray-50 text-gray-600 border-gray-200",
		Anulada: "bg-red-50 text-red-600 border-red-200",
	}

	return (
		<Link
			to={`/tenders/${tender.id}`}
			className="block p-3 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 transition-colors"
		>
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0 flex-1">
					<p className="text-sm font-medium text-gray-900 line-clamp-2">{tender.name}</p>
					<p className="text-xs text-muted-foreground mt-0.5">{tender.expedient}</p>
				</div>
				<Badge variant="outline" className={`text-[10px] shrink-0 ${statusColors[tender.status] || ""}`}>
					{tender.status}
				</Badge>
			</div>
			<div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
				{tender.budget_no_taxes !== null && tender.budget_no_taxes !== undefined && (
					<span className="flex items-center gap-1">
						<FileText className="h-3 w-3" />
						{formatBudget(tender.budget_no_taxes)}
					</span>
				)}
				{tender.submission_deadline && (
					<span className="flex items-center gap-1">
						<Calendar className="h-3 w-3" />
						{formatDate(tender.submission_deadline)}
					</span>
				)}
			</div>
		</Link>
	)
}

function OrganizationDetailSheet({ orgId, onClose }: { orgId: string | null; onClose: () => void }) {
	const t = useTranslations("contacts")
	const [data, setData] = useState<OrganizationDetail | null>(null)
	const [isLoading, setIsLoading] = useState(false)

	useEffect(() => {
		if (!orgId) { setData(null); return }
		setIsLoading(true)
		api.get<{ success: boolean; data: OrganizationDetail }>(`/tenders/organizations/${orgId}`)
			.then((res) => setData(res.data))
			.catch(() => setData(null))
			.finally(() => setIsLoading(false))
	}, [orgId])

	return (
		<Sheet open={!!orgId} onOpenChange={(open) => { if (!open) onClose() }}>
			<SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
				{isLoading ? (
					<div className="flex justify-center py-16"><LoadingSpinner size="lg" /></div>
				) : data ? (
					<>
						<SheetHeader className="pr-8">
							<div className="flex items-center gap-3">
								<div className="h-10 w-10 rounded-full bg-[#2D8A5E]/10 flex items-center justify-center shrink-0">
									<Building2 className="h-5 w-5 text-[#2D8A5E]" />
								</div>
								<div>
									<SheetTitle className="text-left">{data.organization.name}</SheetTitle>
									<SheetDescription className="text-left">
										{t("detailContacts")}: {data.contacts.length} · {t("detailTenders")}: {data.tenders.length}
									</SheetDescription>
								</div>
							</div>
						</SheetHeader>

						<div className="mt-6 space-y-6">
							{/* Emails */}
							<section>
								<h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
									{t("detailEmails")}
								</h3>
								{data.organization.emails && data.organization.emails.length > 0 ? (
									<div className="space-y-1.5">
										{data.organization.emails.map((email) => (
											<div key={email} className="flex items-center gap-2 text-sm">
												<Mail className="h-3.5 w-3.5 text-[#2D8A5E]" />
												<a href={`mailto:${email}`} className="text-[#2D8A5E] hover:underline">{email}</a>
												<CopyButton text={email} />
											</div>
										))}
									</div>
								) : (
									<p className="text-sm text-muted-foreground">{t("detailNoEmails")}</p>
								)}
							</section>

							{/* Contacts */}
							<section>
								<h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
									{t("detailContacts")} ({data.contacts.length})
								</h3>
								{data.contacts.length > 0 ? (
									<div className="space-y-2">
										{data.contacts.map((c) => (
											<div key={c.id} className="p-3 rounded-lg border border-gray-100 space-y-1">
												<div className="flex items-center gap-2">
													<User className="h-3.5 w-3.5 text-gray-400" />
													<span className="text-sm font-medium capitalize">{c.name.toLowerCase()}</span>
													{c.role && <span className="text-xs text-muted-foreground">— {c.role}</span>}
												</div>
												<div className="flex items-center gap-3 flex-wrap">
													{c.email && (
														<span className="inline-flex items-center gap-1 text-xs">
															<Mail className="h-3 w-3 text-[#2D8A5E]" />
															<a href={`mailto:${c.email}`} className="text-[#2D8A5E] hover:underline">{c.email}</a>
															{c.email_verified && (
																<span title={t("detailEmailVerified")}><CircleCheck className="h-3 w-3 text-emerald-500" /></span>
															)}
														</span>
													)}
													{c.phone && (
														<span className="inline-flex items-center gap-1 text-xs text-gray-600">
															<Phone className="h-3 w-3" />
															{c.phone}
														</span>
													)}
												</div>
												<div className="flex items-center gap-2 text-xs text-muted-foreground">
													<SourceBadge type={c.source_type} />
													<Link
														to={c.source_type === "grant" ? `/grants/${c.source_id}` : `/tenders/${c.source_id}`}
														className="hover:text-[#2D8A5E] hover:underline"
													>
														{c.source_reference}
													</Link>
												</div>
											</div>
										))}
									</div>
								) : (
									<p className="text-sm text-muted-foreground">{t("detailNoContacts")}</p>
								)}
							</section>

							{/* Tenders */}
							<section>
								<h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
									<SourceBadge type="tender" /> Licitacions ({data.tenders.length})
								</h3>
								{data.tenders.length > 0 ? (
									<div className="space-y-2">
										{data.tenders.map((tender) => (
											<TenderListItem key={tender.id} tender={tender} />
										))}
									</div>
								) : (
									<p className="text-sm text-muted-foreground">{t("detailNoTenders")}</p>
								)}
							</section>

							{/* Grants */}
							<section>
								<h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
									<SourceBadge type="grant" /> Subvencions ({data.grants?.length ?? 0})
								</h3>
								{data.grants && data.grants.length > 0 ? (
									<div className="space-y-2">
										{data.grants.map((grant) => (
											<GrantListItem key={grant.id} grant={grant} />
										))}
									</div>
								) : (
									<p className="text-sm text-muted-foreground">Sense subvencions associades</p>
								)}
							</section>
						</div>
					</>
				) : (
					<div className="flex justify-center py-16">
						<p className="text-muted-foreground">{t("errorLoading")}</p>
					</div>
				)}
			</SheetContent>
		</Sheet>
	)
}

function ContactDetailSheet({ contactId, onClose }: { contactId: string | null; onClose: () => void }) {
	const t = useTranslations("contacts")
	const [data, setData] = useState<ContactDetailData | null>(null)
	const [isLoading, setIsLoading] = useState(false)

	useEffect(() => {
		if (!contactId) { setData(null); return }
		setIsLoading(true)
		api.get<{ success: boolean; data: ContactDetailData }>(`/tenders/contacts/${contactId}`)
			.then((res) => setData(res.data))
			.catch(() => setData(null))
			.finally(() => setIsLoading(false))
	}, [contactId])

	return (
		<Sheet open={!!contactId} onOpenChange={(open) => { if (!open) onClose() }}>
			<SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
				{isLoading ? (
					<div className="flex justify-center py-16"><LoadingSpinner size="lg" /></div>
				) : data ? (
					<>
						<SheetHeader className="pr-8">
							<div className="flex items-center gap-3">
								<div className="h-10 w-10 rounded-full bg-[#2D8A5E]/10 flex items-center justify-center shrink-0">
									<User className="h-5 w-5 text-[#2D8A5E]" />
								</div>
								<div>
									<SheetTitle className="text-left capitalize">{data.contact.name.toLowerCase()}</SheetTitle>
									<SheetDescription className="text-left">
										{data.contact.role || data.contact.organization_name || ""}
									</SheetDescription>
								</div>
							</div>
						</SheetHeader>

						<div className="mt-6 space-y-6">
							{/* Contact info */}
							<section className="space-y-3">
								{data.contact.organization_name && (
									<div className="flex items-center gap-2 text-sm">
										<Building2 className="h-4 w-4 text-gray-400 shrink-0" />
										<span className="font-medium">{t("detailOrganization")}:</span>
										<span className="text-gray-700">{data.contact.organization_name}</span>
									</div>
								)}
								{data.contact.role && (
									<div className="flex items-center gap-2 text-sm">
										<User className="h-4 w-4 text-gray-400 shrink-0" />
										<span className="font-medium">{t("detailRole")}:</span>
										<span className="text-gray-700">{data.contact.role}</span>
									</div>
								)}
								{data.contact.email && (
									<div className="flex items-center gap-2 text-sm">
										<Mail className="h-4 w-4 text-[#2D8A5E] shrink-0" />
										<span className="font-medium">{t("detailPersonalEmail")}:</span>
										<a href={`mailto:${data.contact.email}`} className="text-[#2D8A5E] hover:underline">{data.contact.email}</a>
										{data.contact.email_verified && (
											<Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-200">
												{t("detailEmailVerified")}
											</Badge>
										)}
										<CopyButton text={data.contact.email} />
									</div>
								)}
								{data.contact.phone && (
									<div className="flex items-center gap-2 text-sm">
										<Phone className="h-4 w-4 text-gray-400 shrink-0" />
										<span className="font-medium">{t("detailPhone")}:</span>
										<a href={`tel:${data.contact.phone}`} className="text-gray-700 hover:underline">{data.contact.phone}</a>
									</div>
								)}
								{data.contact.email_search_source && (
									<div className="flex items-center gap-2 text-sm">
										<Search className="h-4 w-4 text-gray-400 shrink-0" />
										<span className="font-medium">{t("detailSource")}:</span>
										<Badge variant="outline" className="text-[10px]">{data.contact.email_search_source}</Badge>
									</div>
								)}
								{/* Pipedrive sync info */}
								{data.contact.pipedrive_synced_at && (
									<div className="flex items-center gap-2 text-sm">
										<CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
										<span className="font-medium">{t("pipedriveSynced")}:</span>
										<Badge variant="outline" className="text-[10px] text-green-700 border-green-200 bg-green-50">
											{formatDate(data.contact.pipedrive_synced_at)}
										</Badge>
									</div>
								)}
							</section>

							{/* Tenders */}
							<section>
								<h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
									<SourceBadge type="tender" /> Licitacions ({data.tenders.length})
								</h3>
								{data.tenders.length > 0 ? (
									<div className="space-y-2">
										{data.tenders.map((tender) => (
											<TenderListItem key={tender.id} tender={tender} />
										))}
									</div>
								) : (
									<p className="text-sm text-muted-foreground">{t("detailNoTenders")}</p>
								)}
							</section>

							{/* Grants */}
							<section>
								<h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
									<SourceBadge type="grant" /> Subvencions ({data.grants?.length ?? 0})
								</h3>
								{data.grants && data.grants.length > 0 ? (
									<div className="space-y-2">
										{data.grants.map((grant) => (
											<GrantListItem key={grant.id} grant={grant} />
										))}
									</div>
								) : (
									<p className="text-sm text-muted-foreground">Sense subvencions associades</p>
								)}
							</section>
						</div>
					</>
				) : (
					<div className="flex justify-center py-16">
						<p className="text-muted-foreground">{t("errorLoading")}</p>
					</div>
				)}
			</SheetContent>
		</Sheet>
	)
}
